import React, { useState, useEffect, useCallback, useRef } from "react";
import "./App.css";
import { Toaster, toast } from "sonner";
import { Trophy, Check, AlertCircle, Users, ChevronRight, Share2, Copy, Settings, Save, Calculator, Plus, Download, Trash2, Mail } from "lucide-react";
import html2canvas from "html2canvas";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URLs
const WALTERSTOWN_LOGO = "https://customer-assets.emergentagent.com/job_moltbot-setup-4m8p1-7/artifacts/ne5bnijw_walterstown%20logo.jpg";
const MASTERS_LOGO = "https://customer-assets.emergentagent.com/job_moltbot-setup-4m8p1-7/artifacts/zy4jhzay_Masters_Logo.png";

// Sponsor Logos
const LYDON_FARRELL_LOGO = "https://customer-assets.emergentagent.com/job_moltbot-setup-4m8p1-7/artifacts/j0b82dbo_lydon%20farrell.jpg";
const BELLINTER_HOUSE_LOGO = "https://customer-assets.emergentagent.com/job_moltbot-setup-4m8p1-7/artifacts/0kjv4kzz_bellinter%20house.png";
const ROYAL_TARA_LOGO = "https://customer-assets.emergentagent.com/job_moltbot-setup-4m8p1-7/artifacts/6ofnfz8k_tarac.jpeg";

// Country flag mapping
const FLAGS = {
  USA: "🇺🇸", NIR: "🇬🇧", ESP: "🇪🇸", SWE: "🇸🇪", NOR: "🇳🇴", ENG: "🏴󠁧󠁢󠁥󠁮󠁧󠁿",
  JPN: "🇯🇵", IRL: "🇮🇪", AUS: "🇦🇺", AUT: "🇦🇹", CAN: "🇨🇦", KOR: "🇰🇷",
  CHI: "🇨🇱", GER: "🇩🇪", SCO: "🏴󠁧󠁢󠁳󠁣󠁴󠁿", DEN: "🇩🇰", BEL: "🇧🇪", NZL: "🇳🇿",
  RSA: "🇿🇦", CHN: "🇨🇳", COL: "🇨🇴", MEX: "🇲🇽", ARG: "🇦🇷", THA: "🇹🇭",
  FIJ: "🇫🇯", FIN: "🇫🇮"
};

// Share Entry component
function ShareEntry({ entry, onClose }) {
  const shareText = entry 
    ? `I just entered the Walterstown GFC Masters Sweepstake! My picks: ${entry.golfers?.map(g => g.name).join(', ')} (Combined odds: ${entry.combined_odds}). Enter now!`
    : "Enter the Walterstown GFC Masters Sweepstake! Pick 4 golfers and win big!";
  
  const shareUrl = window.location.origin;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
    toast.success('Copied to clipboard!');
  };

  const handleTwitterShare = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleFacebookShare = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleWhatsAppShare = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
        <h2 className="modal-title heading" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Share2 size={24} /> Share Your Entry
        </h2>
        
        <p style={{ marginBottom: '1.5rem', color: 'var(--text-muted)' }}>
          Spread the word and get more people to join the sweepstake!
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button 
            className="btn" 
            onClick={handleWhatsAppShare}
            style={{ background: '#25D366', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Share on WhatsApp
          </button>

          <button 
            className="btn" 
            onClick={handleTwitterShare}
            style={{ background: '#1DA1F2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Share on X
          </button>

          <button 
            className="btn" 
            onClick={handleFacebookShare}
            style={{ background: '#4267B2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Share on Facebook
          </button>

          <button 
            className="btn" 
            onClick={handleCopyLink}
            style={{ background: 'var(--background-alt)', color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <Copy size={20} /> Copy Link
          </button>
        </div>

        <button 
          className="btn" 
          onClick={onClose} 
          style={{ width: '100%', marginTop: '1rem', background: 'transparent', border: '1px solid var(--border)' }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

// Countdown component
function Countdown({ deadline }) {
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const calculateTime = () => {
      const now = new Date();
      const end = new Date(deadline);
      const diff = end - now;

      if (diff <= 0) {
        return { days: 0, hours: 0, mins: 0, secs: 0, expired: true };
      }

      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        mins: Math.floor((diff / (1000 * 60)) % 60),
        secs: Math.floor((diff / 1000) % 60),
        expired: false
      };
    };

    setTimeLeft(calculateTime());
    const timer = setInterval(() => setTimeLeft(calculateTime()), 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  if (timeLeft.expired) {
    return <div className="countdown-expired">Entries Closed</div>;
  }

  return (
    <div className="countdown" data-testid="countdown">
      <div className="countdown-item">
        <div className="countdown-value">{timeLeft.days || 0}</div>
        <div className="countdown-label">Days</div>
      </div>
      <div className="countdown-item">
        <div className="countdown-value">{timeLeft.hours || 0}</div>
        <div className="countdown-label">Hours</div>
      </div>
      <div className="countdown-item">
        <div className="countdown-value">{timeLeft.mins || 0}</div>
        <div className="countdown-label">Mins</div>
      </div>
      <div className="countdown-item">
        <div className="countdown-value">{timeLeft.secs || 0}</div>
        <div className="countdown-label">Secs</div>
      </div>
    </div>
  );
}

// Golfer Card component
function GolferCard({ golfer, isSelected, onSelect, disabled }) {
  return (
    <div
      className={`golfer-card ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
      onClick={() => !disabled && onSelect(golfer)}
      data-testid={`golfer-card-${golfer.id}`}
    >
      <div className="golfer-info">
        <div>
          <div className="golfer-name">{golfer.name}</div>
          <div className="golfer-country">{golfer.country}</div>
        </div>
      </div>
      <div className="golfer-odds">{golfer.odds}</div>
    </div>
  );
}

// Entry Form Modal
function EntryModal({ selectedGolfers, combinedOdds, onClose, onSubmit, isSubmitting }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error('Please fill in name and email');
      return;
    }
    onSubmit({ name: name.trim(), email: email.trim(), phone: phone.trim() });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="entry-modal">
        <h2 className="modal-title heading">Complete Your Entry</h2>
        
        <div className="selected-summary">
          <h4 style={{ margin: '0 0 0.75rem', fontWeight: 600 }}>Your Selection</h4>
          {selectedGolfers.map(g => (
            <div key={g.id} className="selected-golfer">
              <span>{FLAGS[g.country]} {g.name}</span>
              <span>{g.odds}</span>
            </div>
          ))}
          <div className="total-odds">
            <span>Combined Odds</span>
            <span>{combinedOdds}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your full name"
              required
              data-testid="input-name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              data-testid="input-email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Phone (optional)</label>
            <input
              type="tel"
              className="form-input"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="083 123 4567"
              data-testid="input-phone"
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="button" className="btn" onClick={onClose} style={{ flex: 1, background: '#e5e7eb' }}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-gold" 
              style={{ flex: 2 }}
              disabled={isSubmitting}
              data-testid="pay-button"
            >
              {isSubmitting ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <span className="spinner" /> Processing...
                </span>
              ) : (
                `Pay €10 & Enter`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Success Page
function SuccessPage({ sessionId }) {
  const [status, setStatus] = useState('checking');
  const [entry, setEntry] = useState(null);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
        const data = await res.json();
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          // Fetch entry details from metadata
          if (data.metadata?.entry_id) {
            const entryRes = await fetch(`${API_URL}/api/entries/${data.metadata.entry_id}`);
            if (entryRes.ok) {
              setEntry(await entryRes.json());
            }
          }
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else {
          // Keep polling
          setTimeout(checkStatus, 2000);
        }
      } catch (err) {
        console.error('Error checking status:', err);
        setTimeout(checkStatus, 2000);
      }
    };

    checkStatus();
  }, [sessionId]);

  if (status === 'checking') {
    return (
      <div className="success-page">
        <div className="spinner" style={{ margin: '0 auto 2rem', borderColor: 'var(--primary)', borderTopColor: 'var(--accent)' }} />
        <h2 className="heading" style={{ color: 'var(--primary)' }}>Confirming your payment...</h2>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="success-page">
        <div className="success-icon" style={{ background: 'var(--error)' }}>
          <AlertCircle size={40} color="white" />
        </div>
        <h1 className="success-title">Payment Expired</h1>
        <p>Your payment session expired. Please try again.</p>
        <button className="btn btn-primary" onClick={() => window.location.href = '/'} style={{ marginTop: '2rem' }}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="success-page" data-testid="success-page">
      <div className="success-icon">
        <Check size={40} color="white" />
      </div>
      <h1 className="success-title">Entry Confirmed!</h1>
      <p style={{ fontSize: '1.125rem', marginBottom: '2rem' }}>
        Good luck in the Masters Sweepstake!
      </p>
      
      {entry && (
        <div className="selected-summary" style={{ maxWidth: '400px', margin: '0 auto 2rem', textAlign: 'left' }}>
          <h4 style={{ margin: '0 0 0.75rem', fontWeight: 600 }}>{entry.name}'s Selection</h4>
          {entry.golfers?.map(g => (
            <div key={g.id} className="selected-golfer">
              <span>{FLAGS[g.country]} {g.name}</span>
              <span>{g.odds}</span>
            </div>
          ))}
          <div className="total-odds">
            <span>Combined Odds</span>
            <span>{entry.combined_odds}</span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button 
          className="btn btn-gold" 
          onClick={() => setShowShare(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Share2 size={18} /> Share Your Entry
        </button>
        <button 
          className="btn btn-primary" 
          onClick={() => window.location.href = '/'}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Plus size={18} /> Enter Again
        </button>
      </div>

      {showShare && <ShareEntry entry={entry} onClose={() => setShowShare(false)} />}
    </div>
  );
}

// Admin Page
function AdminPage() {
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [golfers, setGolfers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [lastPlaceScore, setLastPlaceScore] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (res.ok) {
        setIsLoggedIn(true);
        fetchGolfers();
        fetchEntries();
        toast.success('Logged in successfully');
      } else {
        toast.error('Invalid password');
      }
    } catch (err) {
      toast.error('Login failed');
    }
  };

  const fetchGolfers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/golfers`);
      const data = await res.json();
      setGolfers(data.golfers || []);
    } catch (err) {
      toast.error('Failed to load golfers');
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/entries`);
      const data = await res.json();
      setEntries(data.entries || []);
    } catch (err) {
      toast.error('Failed to load entries');
    }
  };

  const updateGolferScore = (golferId, field, value) => {
    setGolfers(prev => prev.map(g => {
      if (g.id === golferId) {
        return { ...g, [field]: value };
      }
      return g;
    }));
  };

  const saveScores = async () => {
    setIsSaving(true);
    try {
      const scoresToSave = golfers.map(g => ({
        golfer_id: g.id,
        score: g.score !== null && g.score !== '' ? parseInt(g.score) : null,
        missed_cut: g.missed_cut || false,
        withdrawn: g.withdrawn || false
      }));

      const res = await fetch(`${API_URL}/api/admin/scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scoresToSave)
      });

      if (res.ok) {
        toast.success('Scores saved successfully');
      } else {
        toast.error('Failed to save scores');
      }
    } catch (err) {
      toast.error('Error saving scores');
    }
    setIsSaving(false);
  };

  const saveLastPlaceScore = async () => {
    if (!lastPlaceScore) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/set-last-place-score?score=${lastPlaceScore}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Last place score saved');
      }
    } catch (err) {
      toast.error('Failed to save last place score');
    }
  };

  const clearAllScores = async () => {
    if (!window.confirm('Are you sure you want to clear all golfer scores? This will reset scores for the next day.')) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/admin/clear-scores`, {
        method: 'POST'
      });
      if (res.ok) {
        // Reset local state
        setGolfers(prev => prev.map(g => ({ ...g, score: null, missed_cut: false, withdrawn: false })));
        setLeaderboard([]);
        toast.success('All scores cleared');
      } else {
        toast.error('Failed to clear scores');
      }
    } catch (err) {
      toast.error('Error clearing scores');
    }
  };

  const calculateResults = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/calculate-results`);
      const data = await res.json();
      setLeaderboard(data.leaderboard || []);
      if (data.last_place_score) {
        setLastPlaceScore(data.last_place_score);
      }
      toast.success('Results calculated!');
    } catch (err) {
      toast.error('Failed to calculate results');
    }
    setIsLoading(false);
  };

  const leaderboardRef = useRef(null);

  const downloadLeaderboardImage = async () => {
    if (!leaderboardRef.current || leaderboard.length === 0) {
      toast.error('Calculate results first to generate leaderboard');
      return;
    }
    
    try {
      const canvas = await html2canvas(leaderboardRef.current, {
        backgroundColor: '#0a4a2e',
        scale: 2,
      });
      
      const link = document.createElement('a');
      link.download = `masters-sweepstake-leaderboard-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Leaderboard image downloaded!');
    } catch (err) {
      toast.error('Failed to generate image');
    }
  };

  const [isSendingEmails, setIsSendingEmails] = useState(false);

  const sendResultsEmail = async () => {
    if (!window.confirm('This will send the final results email to ALL entrants. Are you sure?')) {
      return;
    }
    setIsSendingEmails(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/send-results-email`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Results emailed to ${data.sent} entrants!`);
        if (data.failed > 0) {
          toast.warning(`${data.failed} emails failed to send`);
        }
      } else {
        toast.error(data.message || 'Failed to send emails');
      }
    } catch (err) {
      toast.error('Error sending emails');
    }
    setIsSendingEmails(false);
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <h1 className="heading">Admin Login</h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              className="form-input"
              placeholder="Enter admin password"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }}>
              Login
            </button>
          </form>
          <a href="/" style={{ display: 'block', textAlign: 'center', marginTop: '1rem', color: 'var(--primary)' }}>
            Back to Sweepstake
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1 className="heading">
          <Settings size={28} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
          Admin Dashboard
        </h1>
        <a href="/" className="btn" style={{ background: 'var(--primary)', color: 'white' }}>Back to Site</a>
      </div>

      <div className="admin-container">
        {/* Entries Section */}
        <div className="admin-section">
          <h2 style={{ margin: 0 }}>
            <Users size={22} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Entries ({entries.length})
          </h2>
          {entries.length > 0 ? (
            <div className="leaderboard" style={{ marginTop: '1rem' }}>
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Golfers</th>
                    <th>Odds</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, idx) => (
                    <tr key={entry.id}>
                      <td>{idx + 1}</td>
                      <td>{entry.name}</td>
                      <td style={{ fontSize: '0.85rem' }}>{entry.email || '-'}</td>
                      <td style={{ fontSize: '0.85rem' }}>{entry.golfers?.map(g => g.name).join(', ')}</td>
                      <td>{entry.combined_odds}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', padding: '1rem' }}>No entries yet.</p>
          )}
        </div>

        {/* Last Place Score Setting */}
        <div className="admin-section">
          <h2>Last Place Score (for Missed Cuts)</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Enter the score of the last place finisher on Sunday. This score will be used for anyone who missed the cut.
          </p>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input
              type="number"
              className="form-input"
              style={{ width: '150px' }}
              placeholder="e.g. 12"
              value={lastPlaceScore}
              onChange={e => setLastPlaceScore(e.target.value)}
            />
            <button className="btn btn-primary" onClick={saveLastPlaceScore}>
              Save
            </button>
          </div>
        </div>

        {/* Golfer Scores */}
        <div className="admin-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h2>Golfer Scores</h2>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button 
                className="btn" 
                onClick={clearAllScores}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#dc3545', color: 'white' }}
              >
                <Trash2 size={18} /> Clear All Scores
              </button>
              <button 
                className="btn btn-primary" 
                onClick={saveScores}
                disabled={isSaving}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Save size={18} /> {isSaving ? 'Saving...' : 'Save All Scores'}
              </button>
            </div>
          </div>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Enter cumulative scores (relative to par, e.g., -10 for 10 under, +5 for 5 over). Clear scores before entering the next day's totals.
          </p>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ marginRight: '0.5rem' }}>Sort by: </label>
            <select 
              onChange={(e) => {
                const sortType = e.target.value;
                setGolfers(prev => {
                  const sorted = [...prev];
                  if (sortType === 'name') {
                    sorted.sort((a, b) => a.name.localeCompare(b.name));
                  } else if (sortType === 'odds') {
                    sorted.sort((a, b) => a.odds - b.odds);
                  } else if (sortType === 'score') {
                    sorted.sort((a, b) => {
                      const scoreA = a.score ?? 999;
                      const scoreB = b.score ?? 999;
                      return scoreA - scoreB;
                    });
                  }
                  return sorted;
                });
              }}
              style={{ padding: '0.5rem', borderRadius: '4px', background: 'var(--background-alt)', color: 'white', border: '1px solid var(--border)' }}
            >
              <option value="odds">Odds (default)</option>
              <option value="name">Name (A-Z)</option>
              <option value="score">Score (low to high)</option>
            </select>
          </div>
          
          <div className="admin-scores-grid">
            <div className="admin-scores-header">
              <span>Golfer</span>
              <span>Odds</span>
              <span>Score</span>
              <span>MC</span>
              <span>WD</span>
            </div>
            {golfers.map(golfer => (
              <div key={golfer.id} className="admin-scores-row">
                <span className="golfer-name-admin">{golfer.name}</span>
                <span className="golfer-odds-admin">{golfer.odds}</span>
                <input
                  type="number"
                  className="score-input"
                  placeholder="-"
                  value={golfer.score ?? ''}
                  onChange={e => updateGolferScore(golfer.id, 'score', e.target.value)}
                  disabled={golfer.missed_cut || golfer.withdrawn}
                />
                <input
                  type="checkbox"
                  checked={golfer.missed_cut || false}
                  onChange={e => updateGolferScore(golfer.id, 'missed_cut', e.target.checked)}
                />
                <input
                  type="checkbox"
                  checked={golfer.withdrawn || false}
                  onChange={e => updateGolferScore(golfer.id, 'withdrawn', e.target.checked)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Calculate Results */}
        <div className="admin-section">
          <h2>Calculate Final Results</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Once all scores are entered, click below to calculate the final leaderboard and determine winners.
          </p>
          <button 
            className="btn btn-gold" 
            onClick={calculateResults}
            disabled={isLoading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Calculator size={18} /> {isLoading ? 'Calculating...' : 'Calculate Results'}
          </button>
        </div>

        {/* Leaderboard Preview */}
        {leaderboard.length > 0 && (
          <div className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h2>Leaderboard</h2>
              <button 
                className="btn btn-gold" 
                onClick={downloadLeaderboardImage}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Download size={18} /> Download for Socials
              </button>
              <button 
                className="btn" 
                onClick={sendResultsEmail}
                disabled={isSendingEmails}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#0066cc', color: 'white' }}
              >
                <Mail size={18} /> {isSendingEmails ? 'Sending...' : 'Email Results to All'}
              </button>
            </div>
            
            {/* Shareable Leaderboard - styled for social media */}
            <div 
              ref={leaderboardRef} 
              className="shareable-leaderboard"
              style={{
                background: 'linear-gradient(135deg, #0a4a2e 0%, #1a6b4a 100%)',
                padding: '2rem',
                borderRadius: '12px',
                color: 'white'
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  <img src={WALTERSTOWN_LOGO} alt="Walterstown GFC" style={{ height: '50px', borderRadius: '8px' }} />
                  <img src={MASTERS_LOGO} alt="The Masters" style={{ height: '50px' }} />
                </div>
                <h2 style={{ margin: '0.5rem 0', fontSize: '1.5rem', color: '#ffd700' }}>Masters Sweepstake 2026</h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>Leaderboard Update</p>
              </div>
              
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>#</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>Golfers</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.slice(0, 15).map((entry, idx) => (
                    <tr 
                      key={entry.entry_id} 
                      style={{ 
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        background: idx === 0 ? 'rgba(255,215,0,0.2)' : idx === 1 ? 'rgba(192,192,192,0.15)' : idx === 2 ? 'rgba(205,127,50,0.15)' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '0.6rem 0.5rem', fontWeight: idx < 3 ? 'bold' : 'normal' }}>
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                      </td>
                      <td style={{ padding: '0.6rem 0.5rem' }}>{entry.name}</td>
                      <td style={{ padding: '0.6rem 0.5rem', fontSize: '0.85rem', opacity: 0.9 }}>
                        {entry.scores_breakdown?.map((s, i) => {
                          const isReplaced = s.status?.includes('Replaced by');
                          const replacementName = isReplaced ? s.status.split('Replaced by ')[1] : null;
                          return (
                            <span key={i}>
                              {i > 0 && ', '}
                              {isReplaced ? (
                                <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{s.golfer}</span>
                              ) : s.golfer}
                              {replacementName && <span style={{ color: '#ffd700' }}> → {replacementName}</span>}
                            </span>
                          );
                        }) || entry.golfers?.map(g => g.name).join(', ')}
                      </td>
                      <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', fontWeight: 'bold' }}>{entry.total_score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {leaderboard.length > 15 && (
                <p style={{ textAlign: 'center', marginTop: '1rem', opacity: 0.7, fontSize: '0.85rem' }}>
                  + {leaderboard.length - 15} more entries
                </p>
              )}
              
              <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.8rem', opacity: 0.6 }}>
                sweepstake.walterstown.com
              </p>
            </div>

            {/* Full leaderboard table for admin */}
            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Full Results</h3>
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Name</th>
                  <th>Golfers</th>
                  <th>Total Score</th>
                  <th>Combined Odds</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry, idx) => (
                  <tr key={entry.entry_id} className={idx < 3 ? `rank-${idx + 1}` : ''}>
                    <td>
                      {entry.rank === 1 && '🥇 '}
                      {entry.rank === 2 && '🥈 '}
                      {entry.rank === 3 && '🥉 '}
                      {entry.rank}
                    </td>
                    <td>{entry.name}</td>
                    <td>
                      {entry.scores_breakdown?.map((s, i) => {
                        const isReplaced = s.status?.includes('Replaced by');
                        const replacementName = isReplaced ? s.status.split('Replaced by ')[1] : null;
                        const isMissedCut = s.status === 'Missed Cut';
                        return (
                          <div key={i} style={{ fontSize: '0.85rem', marginBottom: '2px' }}>
                            {isReplaced ? (
                              <>
                                <span style={{ textDecoration: 'line-through', opacity: 0.5 }}>{s.golfer}</span>
                                <span style={{ color: '#ffd700', fontWeight: 'bold' }}> → {replacementName}</span>
                                <span style={{ opacity: 0.7 }}> ({s.score})</span>
                              </>
                            ) : (
                              <>
                                {s.golfer}: {s.score}
                                {isMissedCut && <span style={{ color: '#ff6b6b' }}> (MC)</span>}
                                {s.status === 'Completed' && <span style={{ color: '#4ade80' }}> ✓</span>}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </td>
                    <td style={{ fontWeight: 700 }}>{entry.total_score}</td>
                    <td>{entry.combined_odds}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Main App
function App() {
  const [golfers, setGolfers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [entries, setEntries] = useState([]);
  const [deadline, setDeadline] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [minOdds, setMinOdds] = useState(120);
  const [takenCombinations, setTakenCombinations] = useState(new Set());
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingResults, setLoadingResults] = useState(true);

  // Check if returning from Stripe
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id');
  
  // Check if on admin page
  const isAdminPage = window.location.pathname === '/admin';

  // Fetch initial data
  useEffect(() => {
    if (isAdminPage) return; // Don't fetch for admin page
    const fetchData = async () => {
      try {
        // Fetch golfers
        const golfersRes = await fetch(`${API_URL}/api/golfers`);
        const golfersData = await golfersRes.json();
        setGolfers(golfersData.golfers || []);
        setMinOdds(golfersData.min_combined_odds || 120);

        // Fetch deadline
        const deadlineRes = await fetch(`${API_URL}/api/deadline`);
        const deadlineData = await deadlineRes.json();
        setDeadline(deadlineData.deadline);
        setIsClosed(deadlineData.is_closed);

        // Fetch entries
        const entriesRes = await fetch(`${API_URL}/api/entries`);
        const entriesData = await entriesRes.json();
        setEntries(entriesData.entries || []);
        
        // Build taken combinations set
        const taken = new Set();
        (entriesData.entries || []).forEach(e => {
          if (e.golfer_ids) {
            taken.add(e.golfer_ids.sort().join('-'));
          }
        });
        setTakenCombinations(taken);

        // Fetch results (if available)
        const resultsRes = await fetch(`${API_URL}/api/results`);
        const resultsData = await resultsRes.json();
        if (resultsData.has_results) {
          setResults(resultsData.leaderboard || []);
        }
        
        // Fetch full leaderboard for public display
        try {
          const leaderboardRes = await fetch(`${API_URL}/api/admin/calculate-results`);
          const leaderboardData = await leaderboardRes.json();
          if (leaderboardData.leaderboard) {
            setLeaderboard(leaderboardData.leaderboard);
          }
        } catch (err) {
          console.log('Leaderboard not available yet');
        }
        setLoadingResults(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Failed to load data');
      }
    };

    fetchData();
  }, []);

  // Handle golfer selection
  const handleSelectGolfer = useCallback((golfer) => {
    setSelectedIds(prev => {
      if (prev.includes(golfer.id)) {
        return prev.filter(id => id !== golfer.id);
      }
      if (prev.length >= 4) {
        toast.error('You can only select 4 golfers');
        return prev;
      }
      return [...prev, golfer.id];
    });
  }, []);

  // Calculate combined odds
  const selectedGolfers = golfers.filter(g => selectedIds.includes(g.id));
  const combinedOdds = selectedGolfers.reduce((sum, g) => sum + g.odds, 0);
  const isValidOdds = combinedOdds >= minOdds;
  const currentComboKey = [...selectedIds].sort().join('-');
  const isCombinationTaken = selectedIds.length === 4 && takenCombinations.has(currentComboKey);

  // Handle entry submission
  const handleSubmitEntry = async (formData) => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          golfer_ids: selectedIds,
          origin_url: window.location.origin
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create entry');
      }

      const data = await res.json();
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message);
      setIsSubmitting(false);
    }
  };

  // If on admin page
  if (isAdminPage) {
    return (
      <div className="App">
        <Toaster richColors position="top-center" />
        <AdminPage />
      </div>
    );
  }

  // If returning from Stripe payment
  if (sessionId) {
    return (
      <div className="App">
        <Toaster richColors position="top-center" />
        <SuccessPage sessionId={sessionId} />
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster richColors position="top-center" data-testid="toaster" />

      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          {/* Logos */}
          <div className="hero-logos">
            <img src={WALTERSTOWN_LOGO} alt="Walterstown GFC" className="hero-logo" />
            <img src={MASTERS_LOGO} alt="The Masters" className="hero-logo masters-logo" />
          </div>
          
          <h1 className="heading">Walterstown GFC</h1>
          <p className="subtitle">Masters Sweepstake 2026</p>
          
          <p style={{ opacity: 0.9, marginTop: '1rem', fontSize: '1.2rem' }}>
            Thank you for entering!
          </p>
        </div>
      </section>

      <div className="main-container">
        {/* Winners Section */}
        {leaderboard.length > 0 && (
          <>
            <h2 className="section-title heading" style={{ marginBottom: '1.5rem' }}>
              <Trophy size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Our Winners!
            </h2>
            
            <div className="prizes-grid" style={{ marginBottom: '3rem' }}>
              {/* 1st Place */}
              {leaderboard[0] && (
                <div className="prize-card first" data-testid="winner-1st" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(255,215,0,0.05) 100%)', border: '2px solid #ffd700' }}>
                  <img src={LYDON_FARRELL_LOGO} alt="Lydon Farrell Property" className="sponsor-logo" />
                  <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🥇</div>
                  <div className="prize-place" style={{ color: '#ffd700', fontSize: '1.3rem' }}>1st Place</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5rem 0', color: '#0a4a2e' }}>{leaderboard[0].name}</div>
                  <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                    Score: {leaderboard[0].total_score} <span style={{ opacity: 0.7, fontSize: '0.9rem' }}>| Odds: {leaderboard[0].combined_odds}</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '1rem' }}>
                    {leaderboard[0].scores_breakdown?.map((s, i) => {
                      const isReplaced = s.status?.includes('Replaced by');
                      const replacementName = isReplaced ? s.status.split('Replaced by ')[1] : null;
                      return (
                        <span key={i}>
                          {i > 0 && ', '}
                          {isReplaced ? <><s style={{opacity:0.5}}>{s.golfer}</s> → {replacementName}</> : s.golfer}
                        </span>
                      );
                    }) || leaderboard[0].golfers?.map(g => g.name).join(', ')}
                  </div>
                  <div className="prize-value" style={{ color: '#0a4a2e' }}>€300 Cash</div>
                  <div className="prize-sponsor">Sponsored by Lydon Farrell Property</div>
                </div>
              )}
              
              {/* 2nd Place */}
              {leaderboard[1] && (
                <div className="prize-card" data-testid="winner-2nd" style={{ background: 'linear-gradient(135deg, rgba(192,192,192,0.2) 0%, rgba(192,192,192,0.05) 100%)', border: '2px solid #c0c0c0' }}>
                  <img src={BELLINTER_HOUSE_LOGO} alt="Bellinter House Hotel & Spa" className="sponsor-logo bellinter" />
                  <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🥈</div>
                  <div className="prize-place" style={{ color: '#808080', fontSize: '1.3rem' }}>2nd Place</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5rem 0', color: '#0a4a2e' }}>{leaderboard[1].name}</div>
                  <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                    Score: {leaderboard[1].total_score} <span style={{ opacity: 0.7, fontSize: '0.9rem' }}>| Odds: {leaderboard[1].combined_odds}</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '1rem' }}>
                    {leaderboard[1].scores_breakdown?.map((s, i) => {
                      const isReplaced = s.status?.includes('Replaced by');
                      const replacementName = isReplaced ? s.status.split('Replaced by ')[1] : null;
                      return (
                        <span key={i}>
                          {i > 0 && ', '}
                          {isReplaced ? <><s style={{opacity:0.5}}>{s.golfer}</s> → {replacementName}</> : s.golfer}
                        </span>
                      );
                    }) || leaderboard[1].golfers?.map(g => g.name).join(', ')}
                  </div>
                  <div className="prize-value" style={{ color: '#0a4a2e' }}>€250 Spa Voucher</div>
                  <div className="prize-sponsor">Thermal Suite at Bellinter House Hotel & Spa</div>
                </div>
              )}
              
              {/* 3rd Place */}
              {leaderboard[2] && (
                <div className="prize-card" data-testid="winner-3rd" style={{ background: 'linear-gradient(135deg, rgba(205,127,50,0.2) 0%, rgba(205,127,50,0.05) 100%)', border: '2px solid #cd7f32' }}>
                  <img src={ROYAL_TARA_LOGO} alt="Royal Tara Golf Club" className="sponsor-logo" />
                  <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🥉</div>
                  <div className="prize-place" style={{ color: '#cd7f32', fontSize: '1.3rem' }}>3rd Place</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5rem 0', color: '#0a4a2e' }}>{leaderboard[2].name}</div>
                  <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                    Score: {leaderboard[2].total_score} <span style={{ opacity: 0.7, fontSize: '0.9rem' }}>| Odds: {leaderboard[2].combined_odds}</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '1rem' }}>
                    {leaderboard[2].scores_breakdown?.map((s, i) => {
                      const isReplaced = s.status?.includes('Replaced by');
                      const replacementName = isReplaced ? s.status.split('Replaced by ')[1] : null;
                      return (
                        <span key={i}>
                          {i > 0 && ', '}
                          {isReplaced ? <><s style={{opacity:0.5}}>{s.golfer}</s> → {replacementName}</> : s.golfer}
                        </span>
                      );
                    }) || leaderboard[2].golfers?.map(g => g.name).join(', ')}
                  </div>
                  <div className="prize-value" style={{ color: '#0a4a2e' }}>4 Ball Round of Golf</div>
                  <div className="prize-sponsor">Worth €180 at Royal Tara Golf Club</div>
                </div>
              )}
            </div>
            
            {/* Full Results */}
            <h2 className="section-title heading" style={{ marginBottom: '1rem' }}>
              Full Results
            </h2>
            <p style={{ textAlign: 'center', opacity: 0.7, marginBottom: '1.5rem' }}>
              {leaderboard.length} entries • Lowest combined score wins • Tiebreaker: Highest odds
            </p>
            
            <div style={{ overflowX: 'auto' }}>
              <table className="leaderboard-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--primary)', color: 'white' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Rank</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Golfers</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Score</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Odds</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry, idx) => (
                    <tr 
                      key={entry.entry_id} 
                      style={{ 
                        borderBottom: '1px solid #eee',
                        background: idx === 0 ? 'rgba(255,215,0,0.1)' : idx === 1 ? 'rgba(192,192,192,0.1)' : idx === 2 ? 'rgba(205,127,50,0.1)' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '0.75rem', fontWeight: idx < 3 ? 'bold' : 'normal' }}>
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : entry.rank}
                      </td>
                      <td style={{ padding: '0.75rem' }}>{entry.name}</td>
                      <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                        {entry.scores_breakdown?.map((s, i) => {
                          const isReplaced = s.status?.includes('Replaced by');
                          const replacementName = isReplaced ? s.status.split('Replaced by ')[1] : null;
                          const isMissedCut = s.status === 'Missed Cut';
                          return (
                            <span key={i}>
                              {i > 0 && ', '}
                              {isReplaced ? (
                                <><s style={{opacity:0.5}}>{s.golfer}</s><span style={{color:'#0a4a2e', fontWeight:'bold'}}> → {replacementName}</span></>
                              ) : (
                                <>{s.golfer}{isMissedCut && <span style={{color:'#dc2626'}}> (MC)</span>}</>
                              )}
                            </span>
                          );
                        }) || entry.golfers?.map(g => g.name).join(', ')}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{entry.total_score}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', opacity: 0.7 }}>{entry.combined_odds}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        
        {loadingResults && leaderboard.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Loading results...</p>
          </div>
        )}
        
        {!loadingResults && leaderboard.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Results will be displayed here once scores are entered.</p>
          </div>
        )}
      </div>

    </div>
  );
}

export default App;
